import { PlaceHolder } from "@/services/smsPlaceHolders/type";
import {
  ActionIcon,
  Button,
  Group,
  Menu,
  Popover,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDots, IconMoodSmile, IconX } from "@tabler/icons-react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { useEffect, useRef, useState } from "react";
import CustomDrawer from "../drawer";
import LoadingPillItems from "../loading/loadingPillItems/loadingPillItems";

export type Props = {
  tags: PlaceHolder[];
  setValue: (value: string) => void;
  defaultValue?: string;
  isLoading?: boolean;
};

function TagTextArea({ setValue, tags, defaultValue, isLoading }: Props) {
  const [open, setOpend] = useState(false);
  const replaceTagsWithValue = () => {
    const htmlContent = chatBoxRef.current?.innerHTML || "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    // Replace tags with placeholders
    tags?.forEach((tag) => {
      const tagElements = Array.from(
        tempDiv.getElementsByClassName("inserted-tag")
      );

      tagElements.forEach((element) => {
        const childSpan = element.querySelector("span");
        if (
          childSpan &&
          childSpan.textContent?.replace("x", "") === tag?.value
        ) {
          const textNode = document.createTextNode(tag?.key || "");
          element.parentNode?.replaceChild(textNode, element);
        }
      });
    });

    // Replace `<br>` and `<div>` tags with `\n` for plain text
    tempDiv.innerHTML = tempDiv.innerHTML.replace(/<br\s*\/?>/gi, "\n");
    tempDiv.innerHTML = tempDiv.innerHTML.replace(/<\/div>\s*<div>/gi, "\n");
    tempDiv.innerHTML = tempDiv.innerHTML.replace(/<\/?div>/gi, "\n");

    // Replace `&nbsp;` with space
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const changeAreaTextMessage = () => {
    setValue(replaceTagsWithValue());
  };

  const renderTagsInText = (text: string) => {
    if (!chatBoxRef.current) return;

    // Clear the chatbox content
    chatBoxRef.current.innerHTML = "";

    // Regular expression to find placeholders
    const regex = /{{(.*?)}}/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const placeholderKey = match[1]; // Extract the key (e.g., FirstName)
      const tag = tags.find((t) => t.key === `{{${placeholderKey}}}`);

      // Add plain text before the match
      if (lastIndex < match.index) {
        const plainText = text.slice(lastIndex, match.index);
        const textNode = document.createTextNode(plainText);
        chatBoxRef.current.appendChild(textNode);
      }

      // Replace placeholder with a tag if it exists
      if (tag) {
        const tagElement = document.createElement("span");
        tagElement.innerText = tag?.value || "";

        // Create a remove button
        const removeButton = document.createElement("button");
        removeButton.innerText = "x";
        removeButton.style.cssText =
          "background: none; border: none; color: white; ";
        removeButton.onclick = () => removeTag(tagElement);
        removeButton.ontouchstart = () => removeTag(tagElement);

        // Append remove button to the tag element
        tagElement.appendChild(removeButton);

        // Style and insert tag
        tagElement.style.cssText =
          "background: #228be6; padding: 0 5px; color: white; border-radius: 5px; font-size: small;";
        tagElement.classList.add("inserted-tag");
        tagElement.setAttribute("contenteditable", "false");

        const spanNode = document.createElement("span");
        spanNode.style.cssText =
          "background: #228be6 ; padding: 0 5px 0 0 ; color: white; border-radius: 5px; font-size: small;";
        spanNode.appendChild(tagElement);
        spanNode.setAttribute("contenteditable", "false");
        spanNode.classList.add("inserted-tag");

        chatBoxRef.current.appendChild(spanNode);
      } else {
        // If no matching tag, keep the placeholder as text
        const textNode = document.createTextNode(match[0]);
        chatBoxRef.current.appendChild(textNode);
      }

      lastIndex = regex.lastIndex; // Update the last processed index
    }

    // Append any remaining text after the last placeholder
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      const textNode = document.createTextNode(remainingText);
      chatBoxRef.current.appendChild(textNode);
    }
  };

  const chatBoxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (chatBoxRef.current) {
      renderTagsInText(defaultValue || "");
    }
    changeAreaTextMessage();
  }, []);

  const insertTagAtCaret = (node: HTMLElement | Text) => {
    chatBoxRef.current?.focus();
    const selection = window.getSelection();
    if (selection) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      if (node instanceof HTMLElement) {
        const spanNode = document.createElement("span");
        spanNode.style.cssText =
          "background: #228be6 ; padding: 0 5px 0 0 ; color: white; border-radius: 5px; font-size: small;";
        spanNode.appendChild(node);
        spanNode.setAttribute("contenteditable", "false");
        spanNode.classList.add("inserted-tag");
        range.insertNode(spanNode);
      } else {
        range.insertNode(node);
      }
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    changeAreaTextMessage();
    chatBoxRef.current?.focus();
  };

  const addToDiv = (tag: PlaceHolder) => {
    const tagElement = document.createElement("span");
    tagElement.innerText = tag?.value || "";
    const removeButton = document.createElement("button");
    removeButton.innerText = "x";
    removeButton.onclick = () => removeTag(tagElement.parentElement);
    removeButton.ontouchstart = () => removeTag(tagElement.parentElement);
    removeButton.style.cssText = "background: none;border: none;color: white;";
    tagElement.appendChild(removeButton);
    insertTagAtCaret(tagElement);

    // Create and insert a space after the tag
    const spaceNode = document.createTextNode(" ");
    insertTagAtCaret(spaceNode);

    closePreview();
  };

  const removeTag = (spanNode: HTMLElement | null) => {
    if (spanNode && spanNode.parentNode) {
      spanNode.parentNode.removeChild(spanNode);
    }
  };

  const theme = useMantineTheme();

  function handleReaction(text: EmojiClickData) {
    chatBoxRef.current?.focus();
    const selection = window.getSelection();
    if (selection) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const tagElement = document.createElement("span");
      tagElement.innerText = text?.emoji || "";
      range.insertNode(tagElement);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    changeAreaTextMessage();
    chatBoxRef.current?.focus();
    setOpend(false);
    console.log("enter", text);
  }
  const [preview, { open: openPreview, close: closePreview }] =
    useDisclosure(false);
  return (
    <>
      <Group wrap="nowrap" gap="xs">
        <Menu shadow="md" opened={open} withinPortal>
          <Menu.Target>
            <ActionIcon variant="light" onClick={() => setOpend(true)} mb="sm">
              <IconMoodSmile />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown bg="gray.4">
            <ActionIcon
              variant="transparent"
              onClick={() => setOpend(false)}
            >
              <IconX />
            </ActionIcon>
            <EmojiPicker
              height={300}
              open={true}
              onEmojiClick={handleReaction}
              searchDisabled
            />
          </Menu.Dropdown>
        </Menu>

        <Stack
          styles={{
            root: {
              overflow: "hidden",
              overflowX: "scroll",
            },
          }}
          pb="xs"
        >
          <Group id="tags" gap="5" wrap="nowrap">
            {isLoading ? (
              <LoadingPillItems count={3} />
            ) : (
              tags?.map((tag, index) =>
                tag?.hasValue ? (
                  <Button
                    type="button"
                    color="yellow"
                    size="compact-xs"
                    key={index}
                    disabled={!tag?.hasValue}
                    radius="sm"
                    miw="fit-content"
                    onClick={() => addToDiv(tag)}
                  >
                    {tag?.value}
                  </Button>
                ) : (
                  <Popover width={200} position="bottom" withArrow shadow="md">
                    <Popover.Target>
                      <Button
                        type="button"
                        size="compact-xs"
                        key={index}
                        radius="sm"
                        color="gray.4"
                        miw="fit-content"
                      >
                        {tag?.value}
                      </Button>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Text size="xs" ta="center">
                        لطفا اطلاعات مربوط به{" "}
                        <Text c="blue" span>
                          {tag.value}
                        </Text>{" "}
                        خود را در پروفایل کاربری کامل نمایید.
                      </Text>
                    </Popover.Dropdown>
                  </Popover>
                )
              )
            )}
          </Group>
        </Stack>

        <ActionIcon
          size="xs"
          mb="xs"
          color="gray"
          variant="light"
          onClick={openPreview}
        >
          <IconDots />
        </ActionIcon>
      </Group>
      <Stack p="0" pos="relative">
        <div
          id="chatbox"
          ref={chatBoxRef}
          contentEditable
          role="textbox"
          onFocus={changeAreaTextMessage}
          onBlur={changeAreaTextMessage}
          onInput={changeAreaTextMessage}
          suppressContentEditableWarning={true}
          style={{
            border: `1px solid ${theme.colors.gray[5]}`,
            padding: "10px",
            height: "250px",
            borderRadius: "5px",
            whiteSpace: "pre-wrap",
          }}
        ></div>

        <Stack py="0">
          <Text size="xs" c="yellow">
            عبارت `لغو 11` به صورت خودکار به انتهای متن اضافه خواهد شد.
          </Text>
        </Stack>
      </Stack>
      <CustomDrawer
        opened={preview}
        onClose={closePreview}
        close={closePreview}
        position="bottom"
        radius="md"
        title=""
        custom="tags"
      >
        <Stack justify="space-between" h={"350px"}>
          <Stack
            styles={{
              root: {
                flexGrow: 1,
              },
            }}
          >
            <Group>
              {tags?.map((tag, index) =>
                tag?.hasValue ? (
                  <Button
                    type="button"
                    color="yellow"
                    size="compact-xs"
                    key={index}
                    disabled={!tag?.hasValue}
                    radius="sm"
                    onClick={() => addToDiv(tag)}
                  >
                    {tag?.value}
                  </Button>
                ) : (
                  <Popover width={200} position="bottom" withArrow shadow="md">
                    <Popover.Target>
                      <Button
                        type="button"
                        size="compact-xs"
                        key={index}
                        radius="sm"
                        color="gray.4"
                      >
                        {tag?.value}
                      </Button>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Text size="xs" ta="center">
                        لطفا اطلاعات مربوط به{" "}
                        <Text c="blue" span>
                          {tag.value}
                        </Text>{" "}
                        خود را در پروفایل کاربری کامل نمایید.
                      </Text>
                    </Popover.Dropdown>
                  </Popover>
                )
              )}
            </Group>
          </Stack>
        </Stack>
      </CustomDrawer>
    </>
  );
}

export default TagTextArea;
